import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'

import { FILTERS } from '../labels'
import { ACCENT, BAND, FONT_MONO, INK, INK_FAINT, INK_SOFT, LINE, PAPER } from '../theme'

export interface FilterState {
  owner: string
  source: string
  status: string
  cycle: string
  period: string
}

export const EMPTY_FILTERS: FilterState = {
  owner: '',
  source: '',
  status: '',
  cycle: '',
  period: '',
}

interface Option {
  value: string
  label: string
}

interface Props {
  value: FilterState
  onChange: (next: FilterState) => void
  owners?: Option[]
  sources?: Option[]
  statuses?: Option[]
  cycles?: Option[]
  periods?: Option[]
}

/**
 * Filtros como píldoras, no como formulario.
 *
 * Sólo se pintan los selectores para los que hay opciones: en la vista de
 * backlog no tiene sentido ofrecer "fuente de datos", y un filtro vacío es
 * ruido que estorba. El brief pide filtros, no un panel de filtros.
 *
 * La píldora lleva su propia etiqueta adentro ("FUENTE: Nielsen"), así que no
 * hace falta una fila de rótulos encima ni un `<label>` flotante: una fila,
 * una altura, cero cromo.
 */
export function FilterBar({ value, onChange, owners, sources, statuses, cycles, periods }: Props) {
  const set = (key: keyof FilterState) => (v: string) => onChange({ ...value, [key]: v })
  const active = Object.values(value).some(Boolean)

  const selects: [keyof FilterState, string, Option[] | undefined][] = [
    ['owner', FILTERS.owner, owners],
    ['source', FILTERS.source, sources],
    ['cycle', FILTERS.cycle, cycles],
    ['status', FILTERS.status, statuses],
    ['period', FILTERS.period, periods],
  ]

  const shown = selects.filter(([, , options]) => options && options.length > 0)
  if (shown.length === 0) return null

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {shown.map(([key, label, options]) => (
        <FilterPill
          key={key}
          label={label}
          value={value[key]}
          options={options!}
          onChange={set(key)}
        />
      ))}

      {active && (
        <ButtonBase
          onClick={() => onChange(EMPTY_FILTERS)}
          sx={{
            borderRadius: 999,
            px: 1.5,
            py: 0.9,
            fontFamily: FONT_MONO,
            fontSize: '0.6875rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: ACCENT,
            '&:hover': { bgcolor: `${ACCENT}12` },
          }}
        >
          {FILTERS.clear}
        </ButtonBase>
      )}
    </Stack>
  )
}

function FilterPill({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: Option[]
  onChange: (v: string) => void
}) {
  const on = Boolean(value)

  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      displayEmpty
      renderValue={(v) => (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Box
            component="span"
            sx={{
              fontFamily: FONT_MONO,
              fontSize: '0.625rem',
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              color: INK_FAINT,
            }}
          >
            {label}
          </Box>
          <Box
            component="span"
            sx={{ fontSize: '0.8125rem', fontWeight: on ? 600 : 400, color: on ? INK : INK_SOFT }}
          >
            {options.find((o) => o.value === v)?.label ?? FILTERS.all}
          </Box>
        </Box>
      )}
      MenuProps={{ MenuListProps: { dense: true }, PaperProps: { sx: { maxHeight: 340 } } }}
      sx={{
        borderRadius: 999,
        bgcolor: on ? PAPER : BAND,
        height: 38,
        '& .MuiSelect-select': { paddingBlock: 0, paddingLeft: '14px' },
        '& fieldset': { borderColor: on ? INK_FAINT : LINE },
        '&:hover fieldset': { borderColor: INK_FAINT },
      }}
    >
      <MenuItem value="">{FILTERS.all}</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  )
}
